import { defineStore } from "pinia";

// Types for search results
export interface TranscriptSearchResult {
  transcriptId: string;
  bookingId: string;
  programId: string;
  score: number;
  relevance: string;
  snippet: string | null;
  callTitle: string | null;
  callBrief: string | null;
  durationSeconds: number | null;
  processedAt: string | null;
  expertName: string | null;
  attendeeName: string | null;
}

export interface SearchResponse {
  results: TranscriptSearchResult[];
  totalCount: number;
  query: string;
  searchTimeMs: number;
}

// Types for trending topics
export interface TrendDataPoint {
  processed_date: string;
  count: number;
}

export interface TrendingTopic {
  topic: string;
  count: number;
  started_at: string | null;
  trend_data: TrendDataPoint[];
  expert_names: string[];
  booking_ids: string[];
}

// State interface
interface InsightsState {
  // Search state
  searchQuery: string;
  searchResults: TranscriptSearchResult[];
  totalResults: number;
  searchTimeMs: number;
  isSearching: boolean;
  searchError: string | null;
  hasPerformedSearch: boolean;

  // Recent searches
  recentSearches: string[];

  // Filters
  selectedProgramIds: string[];
  dateRangeStart: string | null;
  dateRangeEnd: string | null;

  // Trending topics
  trendingTopics: TrendingTopic[];
  isLoadingTrends: boolean;

  // UI State
  activeTab: "search" | "trends";

  // Selected transcript for detail view
  selectedTranscriptId: string | null;
}

export const useInsightsStore = defineStore("insights", {
  state: (): InsightsState => ({
    // Search state
    searchQuery: "",
    searchResults: [],
    totalResults: 0,
    searchTimeMs: 0,
    isSearching: false,
    searchError: null,
    hasPerformedSearch: false,

    // Recent searches (max 5)
    recentSearches: [],

    // Filters
    selectedProgramIds: [],
    dateRangeStart: null,
    dateRangeEnd: null,

    // Trending topics
    trendingTopics: [],
    isLoadingTrends: false,

    // UI State
    activeTab: "search",

    // Selected transcript
    selectedTranscriptId: null,
  }),

  getters: {
    hasResults: (state) => state.searchResults.length > 0,
    hasSearched: (state) => state.hasPerformedSearch,
    recentSearchesLimited: (state) => state.recentSearches.slice(0, 5),
  },

  actions: {
    // Search actions
    setSearchQuery(query: string) {
      this.searchQuery = query;
    },

    setSearchResults(response: SearchResponse) {
      this.searchResults = response.results;
      this.totalResults = response.totalCount;
      this.searchTimeMs = response.searchTimeMs;
      this.searchError = null;
      this.hasPerformedSearch = true;
    },

    setSearching(isSearching: boolean) {
      this.isSearching = isSearching;
    },

    setSearchError(error: string | null) {
      this.searchError = error;
      this.isSearching = false;
    },

    clearSearch() {
      this.searchQuery = "";
      this.searchResults = [];
      this.totalResults = 0;
      this.searchTimeMs = 0;
      this.searchError = null;
      this.hasPerformedSearch = false;
    },

    // Recent searches
    addRecentSearch(query: string) {
      if (!query.trim()) return;

      // Remove if already exists
      this.recentSearches = this.recentSearches.filter(
        (s) => s.toLowerCase() !== query.toLowerCase()
      );

      // Add to beginning
      this.recentSearches.unshift(query);

      // Keep only last 5
      if (this.recentSearches.length > 5) {
        this.recentSearches = this.recentSearches.slice(0, 5);
      }
    },

    clearRecentSearches() {
      this.recentSearches = [];
    },

    // Filter actions
    setSelectedPrograms(programIds: string[]) {
      this.selectedProgramIds = programIds;
    },

    setDateRange(start: string | null, end: string | null) {
      this.dateRangeStart = start;
      this.dateRangeEnd = end;
    },

    clearFilters() {
      this.selectedProgramIds = [];
      this.dateRangeStart = null;
      this.dateRangeEnd = null;
    },

    // Trending topics
    setTrendingTopics(topics: TrendingTopic[]) {
      this.trendingTopics = topics;
    },

    setLoadingTrends(isLoading: boolean) {
      this.isLoadingTrends = isLoading;
    },

    // UI actions
    setActiveTab(tab: "search" | "trends") {
      this.activeTab = tab;
    },

    // Transcript selection
    setSelectedTranscript(transcriptId: string | null) {
      this.selectedTranscriptId = transcriptId;
    },

    // Clear all state
    clearStore() {
      this.searchQuery = "";
      this.searchResults = [];
      this.totalResults = 0;
      this.searchTimeMs = 0;
      this.isSearching = false;
      this.searchError = null;
      this.hasPerformedSearch = false;
      this.selectedProgramIds = [];
      this.dateRangeStart = null;
      this.dateRangeEnd = null;
      this.trendingTopics = [];
      this.isLoadingTrends = false;
      this.activeTab = "search";
      this.selectedTranscriptId = null;
      // Note: We keep recentSearches for UX
    },
  },

  persist: true,
});
